/**
 * Panel Registry — the single canonical-id vocabulary.
 *
 * One registry per document owns every panel id, alias and default group the
 * application declares. It replaces the four duplicated id tables the
 * reference app used to carry (a canonical-id map, a content map with suffix
 * logic, a tab-alias table and a menu id map): `toCanonicalId` resolves
 * instance-prefixed tab ids ("main-panel-color-panel"), menu-level ids
 * ("colors") and registered aliases to ONE canonical id per panel, so
 * visibility flags, menu checkmarks and restore requests stay consistent
 * across dispatchers.
 *
 * Creation also locks the panel storage namespace (first call wins) and
 * optionally imports legacy per-app persistence keys, so it must run at
 * module scope, before any shell component renders.
 */

import type { ReactNode } from 'react';
import { setPanelStorageNamespace } from '../store/namespace';
import { migratePanelStorageKeys } from '../store/migration';

export interface PanelDefinition {
    /** Canonical panel id — no instance prefix, stable across layouts. */
    id: string;
    /** English label (the fallback every resolver starts from). */
    label: string;
    /** Optional translation key resolved through the host's label resolver. */
    labelKey?: string;
    /** App-supplied icon node (the package ships no icon dependency). */
    icon?: ReactNode;
    /** Renders the panel body; called by the shell when the tab mounts. */
    render?: () => ReactNode;
}

export interface PanelTabSpec {
    id: string;
    label?: string;
    labelKey?: string;
    icon?: ReactNode;
}

export interface PanelGroupSpec {
    /** Group id WITHOUT the instance prefix ("history-properties"). */
    id: string;
    /** Tab ids referencing registered panels, or inline specs for
        unregistered single-use tabs. */
    tabs: Array<string | PanelTabSpec>;
}

export interface PanelRegistryOptions {
    /** localStorage namespace every package key derives from. */
    storageNamespace: string;
    /** Legacy per-app keys to import once on first run:
        `{ "cizgi_dock_layout": "dock_layout", … }`. */
    migrateLegacyKeys?: Record<string, string>;
    /** The workspace id a `panel:reset-workspace` restores. Apps with
        named factory presets declare their own ("essentials", …). */
    defaultWorkspaceId?: string;
}

export interface PanelRegistry {
    register: (definition: PanelDefinition) => void;
    unregister: (id: string) => void;
    get: (id: string) => PanelDefinition | undefined;
    list: () => PanelDefinition[];
    /** Register an alternative id that resolves to a canonical one. */
    alias: (aliasId: string, canonicalId: string) => void;
    /** Resolve any id spelling (instance-prefixed, alias, menu id) to the
        canonical panel id. Unknown ids pass through unchanged. */
    toCanonicalId: (id: string) => string;
    /** Declare a panel instance's factory groups. `panelId` is the dock
        entry id ("main-panel", "panel-<n>"). */
    registerDefaultGroups: (panelId: string, groups: PanelGroupSpec[]) => void;
    getDefaultGroups: (panelId: string) => PanelGroupSpec[];
    /** The app's factory workspace id (see PanelRegistryOptions). */
    defaultWorkspaceId: string;
}

export interface PanelRegistryInstance {
    registry: PanelRegistry;
}

let defaultRegistry: PanelRegistry | null = null;

/** The first registry created in this document — the one menu bars and
 * command surfaces read when no registry is passed explicitly. */
export const getPanelRegistry = (): PanelRegistry | null => defaultRegistry;

export function createPanelRegistry(
    options: PanelRegistryOptions,
): PanelRegistry {
    setPanelStorageNamespace(options.storageNamespace);
    if (options.migrateLegacyKeys) {
        migratePanelStorageKeys(options.migrateLegacyKeys);
    }

    const definitions = new Map<string, PanelDefinition>();
    const aliases = new Map<string, string>();
    const defaultGroups = new Map<string, PanelGroupSpec[]>();

    // Sorted once per lookup so suffixed ids ("main-panel-gradient-panel")
    // resolve to the longest matching key instead of a shorter alias.
    let sortedKeys: string[] = [];
    const reindex = () => {
        sortedKeys = [...definitions.keys(), ...aliases.keys()].sort(
            (a, b) => b.length - a.length,
        );
    };

    const registry: PanelRegistry = {
        register(definition) {
            definitions.set(definition.id, definition);
            reindex();
        },
        unregister(id) {
            definitions.delete(id);
            reindex();
        },
        get(id) {
            return definitions.get(id);
        },
        list() {
            return [...definitions.values()];
        },
        alias(aliasId, canonicalId) {
            aliases.set(aliasId, canonicalId);
            reindex();
        },
        toCanonicalId(id) {
            const aliased = aliases.get(id);
            if (aliased) return aliased;
            if (definitions.has(id)) return id;
            const matchedKey = sortedKeys.find(
                (key) => id === key || id.endsWith(`-${key}`),
            );
            if (!matchedKey) return id;
            return aliases.get(matchedKey) ?? matchedKey;
        },
        registerDefaultGroups(panelId, groups) {
            defaultGroups.set(panelId, groups);
        },
        getDefaultGroups(panelId) {
            return defaultGroups.get(panelId) ?? [];
        },
        defaultWorkspaceId: options.defaultWorkspaceId ?? 'default',
    };

    if (!defaultRegistry) defaultRegistry = registry;
    return registry;
}
